import { Stepper } from '@mantine/core';
import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import Button from '../../components/Button';
import { ROUTES } from '../../routes/routes';
import { Onboarding } from '../../types/company';
import { useAxios } from '../../utils/hooks';
import AboutYou from './AboutYouForm';

export default function OnboardingPage() {
    const [active, setActive] = useState(0);
    const axios = useAxios();
    const { addToast } = useToasts();
    const history = useHistory();
    const handleNextStep = () => {
        if (active >= 1) {
            handleCompleted();
        }
        if (active < 2) {
            setActive(active + 1);
        }
    };
    const handleCompleted = useCallback(
        (redirect?: boolean) => {
            if (!axios || !axios.current) {
                return;
            }
            return axios.current?.post<Onboarding>('/company/onboarding', { completedAt: new Date() }).then(
                (res) => {
                    if (redirect) {
                        history.push(ROUTES.DASHBOARD);
                    }
                },
                (err) => {
                    addToast('We did something wrong. Please try again.', { appearance: 'error' });
                }
            );
        },
        [addToast, axios, history]
    );
    const handleCompletedAndRedirect = () => handleCompleted(true);

    return (
        <div className="w-full flex flex-col h-screen overflow-y-hidden">
            <div className="w-full overflow-x-hidden border-t flex flex-col">
                <main className="w-full flex-grow p-4">
                    <div className="relative isolate px-6 pt-14 lg:px-8">
                        <div className="mx-auto max-w-2xl sm:py-4 lg:py-8">
                            <div className="text-center">
                                <Stepper
                                    active={active}
                                    onStepClick={setActive}
                                    breakpoint="sm"
                                    allowNextStepsSelect={false}
                                >
                                    <Stepper.Step label="Tell us about you">
                                        <OnboardingStep
                                            headline={
                                                <>
                                                    Welcome. <br /> Let's get you started.
                                                </>
                                            }
                                            description="What tool is your website built with?"
                                        >
                                            <AboutYou onNextStep={handleNextStep} />
                                        </OnboardingStep>
                                    </Stepper.Step>
                                    <Stepper.Step label="Schedule help">
                                        <OnboardingStep
                                            headline="90% of our successful customers start with a 15 minute integration call."
                                            description={
                                                <>
                                                    <div className="flex space-x-4 content-center">
                                                        <img
                                                            src="https://www.pricewell.com/images/matt.png"
                                                            className="w-10 h-10 overflow-hidden bg-gray-100 rounded-full"
                                                            alt=""
                                                        />
                                                        <span className="font-bold place-self-center">Matt Reid</span>
                                                    </div>
                                                    <div className="text-left font-bold pt-4">
                                                        A free 15-30 minute call with me to:
                                                    </div>
                                                    <ul className="list-disc list-inside py-2 text-left">
                                                        <li> Discuss your current set up and needs</li>
                                                        <li>Figure out how PriceWell can help you </li>
                                                        <li>No sales pitch, no script.</li>
                                                    </ul>{' '}
                                                    <span className="italic">
                                                        "Owner Matt Reid just walked me through the entire setup and
                                                        it's seamless. Cannot recommend highly enough!"
                                                    </span>{' '}
                                                    Milly Barker-DeStefano, Founder Tabitha.ai
                                                </>
                                            }
                                        >
                                            <div className="mt-10 flex items-center justify-center gap-x-6">
                                                <Button onClick={handleNextStep}>Book a Call</Button>
                                                <Button onClick={handleCompletedAndRedirect} kind="outline">
                                                    No thanks
                                                </Button>
                                            </div>
                                        </OnboardingStep>
                                    </Stepper.Step>
                                    <Stepper.Completed>
                                        <OnboardingStep headline="Book your free call">
                                            <iframe
                                                title="Book a quick chat"
                                                src="https://calendly.com/matthew_reid/pricewell?hide_event_type_details=1&amp;embed_domain=www.pricewell.io&amp;embed_type=Inline&amp;hide_gdpr_banner=1"
                                                width="100%"
                                                height="100%"
                                                className="w-full h-full"
                                                style={{ height: '630px' }}
                                                frameBorder={0}
                                            ></iframe>
                                        </OnboardingStep>
                                    </Stepper.Completed>
                                </Stepper>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}

function OnboardingStep(props: {
    headline: React.ReactNode;
    description?: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-4xl">{props.headline}</h1>
            {props.description && <div className="mt-6 text-center leading-8 text-gray-800">{props.description}</div>}
            {props.children}
        </>
    );
}
